
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ProvideJobDescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleTitle: string;
  onSave: () => void;
}

export function ProvideJobDescriptionDialog({
  open,
  onOpenChange,
  roleId,
  roleTitle,
  onSave
}: ProvideJobDescriptionDialogProps) {
  const [jobDescription, setJobDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!jobDescription.trim()) {
      toast.error("Please enter a job description");
      return;
    }

    setSaving(true);
    try {
      const roleDescriptionData = {
        text: jobDescription.trim()
      };

      const { error } = await supabase
        .from('role_jd')
        .update({ 
          role_description: roleDescriptionData,
          has_jd: true
        })
        .eq('id', roleId);

      if (error) throw error;

      toast.success("Job description saved successfully");
      setJobDescription("");
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving job description:', error);
      toast.error("Failed to save job description");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setJobDescription("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            Provide Job Description
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-[11px] text-white">
            Copy and paste the text-based job description for the role in the textarea, then click the Save button.
          </div>

          <div className="space-y-2">
            <Label htmlFor="jobDescription" className="text-primary-light text-[12px]">
              Job Description for {roleTitle}
            </Label>
            <Textarea
              id="jobDescription"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Copy and paste the text-based job description here..."
              className="min-h-[300px] bg-secondary/30 border-primary/20 text-white text-[11px] placeholder:text-white/50 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              onClick={handleCancel}
              variant="outline"
              className="border-primary/20 bg-white text-black px-8"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-primary text-black px-8"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
